import { QuoteQuestion } from '@/api/endpoints/quote/useGenerateQuoteQuestions';

export interface StoredQuoteAnswers {
  answers: Record<number, string | number>;
  questions: QuoteQuestion[];
  timestamp: string;
  serviceType: string;
  searchQuery: string;
  propertyAddress?: string;
}

export class QuoteAnswerSessionService {
  private static readonly STORAGE_PREFIX = 'quote_session_';

  private static readonly SESSION_DURATION_HOURS = 24; // Questions valid for 24 hours

  /**
   * Generate a session key based on search parameters
   */
  private static getSessionKey(searchQuery: string, propertyAddress?: string): string {
    // Create a consistent key for the same search context
    const normalizedQuery = searchQuery.toLowerCase().trim();
    const normalizedAddress = propertyAddress?.toLowerCase().trim() || '';
    return `${this.STORAGE_PREFIX}${btoa(`${normalizedQuery  }_${  normalizedAddress}`)}`;
  }

  /**
   * Save answers for the current search session
   */
  static saveAnswers(
    searchQuery: string,
    answers: Record<number, string | number>,
    questions: QuoteQuestion[],
    serviceType: string,
    propertyAddress?: string
  ): void {
    const sessionKey = this.getSessionKey(searchQuery, propertyAddress);
    const sessionData: StoredQuoteAnswers = {
      answers,
      questions,
      timestamp: new Date().toISOString(),
      serviceType,
      searchQuery,
      propertyAddress,
    };

    try {
      localStorage.setItem(sessionKey, JSON.stringify(sessionData));
      
      // Also save to legacy format for backward compatibility
      localStorage.setItem(`quote_answers_${searchQuery}`, JSON.stringify({
        answers,
        timestamp: sessionData.timestamp,
        serviceType,
      }));
    } catch (error) {
      // Failed to save quote answers to localStorage
    }
  }

  /**
   * Retrieve saved answers for the current search session
   */
  static getSavedAnswers(
    searchQuery: string,
    propertyAddress?: string
  ): StoredQuoteAnswers | null {
    const sessionKey = this.getSessionKey(searchQuery, propertyAddress);

    try {
      const stored = localStorage.getItem(sessionKey);
      if (!stored) {
        return null;
      }

      const sessionData: StoredQuoteAnswers = JSON.parse(stored);
      
      // Check if the session is still valid (within 24 hours)
      const storedTime = new Date(sessionData.timestamp);
      const currentTime = new Date();
      const hoursDiff = (currentTime.getTime() - storedTime.getTime()) / (1000 * 60 * 60);
      
      if (hoursDiff > this.SESSION_DURATION_HOURS) {
        // Session expired, remove it
        this.clearSession(searchQuery, propertyAddress);
        return null;
      }

      return sessionData;
    } catch (error) {
      // Failed to retrieve saved quote answers
      return null;
    }
  }

  /**
   * Check if there are saved answers for the current session
   */
  static hasSavedAnswers(searchQuery: string, propertyAddress?: string): boolean {
    return this.getSavedAnswers(searchQuery, propertyAddress) !== null;
  }

  /**
   * Clear saved answers for the current session
   */
  static clearSession(searchQuery: string, propertyAddress?: string): void {
    const sessionKey = this.getSessionKey(searchQuery, propertyAddress);
    
    try {
      localStorage.removeItem(sessionKey);
      // Also clear legacy format
      localStorage.removeItem(`quote_answers_${searchQuery}`);
    } catch (error) {
      // Failed to clear quote session
    }
  }

  /**
   * Clear all expired sessions
   */
  static clearExpiredSessions(): void {
    try {
      const keys = Object.keys(localStorage);
      const sessionKeys = keys.filter(key => key.startsWith(this.STORAGE_PREFIX));

      sessionKeys.forEach(key => {
        try {
          const stored = localStorage.getItem(key);
          if (stored) {
            const sessionData: StoredQuoteAnswers = JSON.parse(stored);
            const storedTime = new Date(sessionData.timestamp);
            const currentTime = new Date();
            const hoursDiff = (currentTime.getTime() - storedTime.getTime()) / (1000 * 60 * 60);
            
            if (hoursDiff > this.SESSION_DURATION_HOURS) {
              localStorage.removeItem(key);
            }
          }
        } catch (error) {
          // If we can't parse the session data, remove it
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      // Failed to clear expired sessions
    }
  }

  /**
   * Check if saved answers are compatible with current questions
   */
  static areAnswersCompatible(
    savedAnswers: StoredQuoteAnswers,
    currentQuestions: QuoteQuestion[]
  ): boolean {
    // Check if all required questions from current set have answers
    const requiredCurrentQuestions = currentQuestions.filter(q => q.required);
    const savedQuestionIds = new Set(Object.keys(savedAnswers.answers).map(Number));
    
    // All required questions must have answers
    const allRequiredAnswered = requiredCurrentQuestions.every(q => 
      savedQuestionIds.has(q.id) && 
      savedAnswers.answers[q.id] !== undefined && 
      savedAnswers.answers[q.id] !== ''
    );

    return allRequiredAnswered;
  }

  /**
   * Merge saved answers with current questions, filling gaps as needed
   */
  static mergeAnswersWithQuestions(
    savedAnswers: StoredQuoteAnswers,
    currentQuestions: QuoteQuestion[]
  ): {
    answers: Record<number, string | number>;
    missingRequired: QuoteQuestion[];
  } {
    const answers: Record<number, string | number> = { ...savedAnswers.answers };
    const missingRequired: QuoteQuestion[] = [];

    currentQuestions.forEach(question => {
      if (question.required && 
          (!answers[question.id] || answers[question.id] === '')) {
        missingRequired.push(question);
      }
    });

    return {
      answers,
      missingRequired,
    };
  }
}